import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientsAPI, usersAPI } from '../utils/api';
import { PageHeader, SearchInput, EmptyState, ConfirmDialog } from '../components/layout/UI';
import { COUNTRIES, getCities } from '../utils/countriesData';
import { useAuth } from '../hooks/useAuth';
import { isAdminRole } from '../utils/rolesConfig';

import { apiError, useConfirm, useToast } from '../components/Toast';
import { useMaster } from '../hooks/useMasters';
import { usePagePerms } from '../PagePermsContext';
import { Building2, Plus, RotateCcw, Trash2 } from 'lucide-react';
import Pager, { usePaging } from '../components/Pager';
import { useSelection, SelectAll, RowCheck, BulkBar } from '../components/BulkActions';

const PlusIc = () => <Plus size={14} />;

const STATUS_COLOR = { Active:'#10b981', Prospect:'#f59e0b', Inactive:'#94a3b8', Archived:'#64748b' };

const defaultForm = {
  type:'company', company_name:'', registration_number:'', tax_id:'', industry:'',
  website:'', first_name:'', last_name:'', national_id:'', date_of_birth:'', gender:'',
  contact_person:'', email:'', phone:'', phone_alt:'', address:'', city:'', country:'',
  status:'Active', account_manager_id:'', notes:'',
};

// Validation helpers
const validateRegNo = (v) => {
  if (!v) return null;
  if (v.length < 4) return 'Registration number must be at least 4 characters';
  if (!/^[A-Za-z0-9/\-\s]+$/.test(v)) return 'Only letters, numbers, hyphens and slashes allowed';
  return null;
};
const validateTaxId = (v) => {
  if (!v) return null;
  if (v.length < 5) return 'Tax ID must be at least 5 characters';
  if (!/^[A-Za-z0-9\-]+$/.test(v)) return 'Only letters, numbers and hyphens allowed';
  return null;
};

const INDUSTRIES_FALLBACK = ['Banking & Finance','Technology','Healthcare','Education','Manufacturing',
  'Agriculture','Real Estate','Construction','Retail','NGO / Non-Profit',
  'Government','Consulting','Telecommunications','Energy','Transport & Logistics','Other'];

export default function ClientsPage() {
  // Admin → Page Access decides whether this role may create here.
  const { canWrite } = usePagePerms();
  // Admin-controlled list; the array above is the fallback.
  const INDUSTRIES = useMaster('industry', INDUSTRIES_FALLBACK);
  const toast = useToast();
  const confirm = useConfirm();
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [clients,    setClients]    = useState([]);
  const [users,      setUsers]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  // All / Mine / Deleted as one control — the three are mutually exclusive.
  const [scope, setScope] = useState('all');
  // One pager for every list, so they all count and phrase it the same way.
  const paging = usePaging(scope);
  const [typeFilter, setTypeFilter] = useState('');
  const [statFilter, setStatFilter] = useState('');
  const [modal,      setModal]      = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [form,       setForm]       = useState({ ...defaultForm });
  const [saving,     setSaving]     = useState(false);
  const [delId,      setDelId]      = useState(null);
  const [errors,     setErrors]     = useState({});





  const discard = async (row) => {
    const ok = await confirm(
      'Remove this from your Deleted tab? You will not be able to see it after '
      + 'this, though an administrator can still restore it.',
      { title: 'Remove from Deleted', confirmLabel: 'Remove', danger: true });
    if (!ok) return;
    try {
      await clientsAPI.discard(row.id);
      toast.success('Removed from your Deleted tab');
      load();
    } catch (e) { toast.error(apiError(e, 'Could not remove it.')); }
  };
  const restore = async (row) => {
    try {
      await clientsAPI.restore(row.id);
      toast.success('Restored');
      load();
    } catch (e) { toast.error(apiError(e, 'Could not restore that client.')); }
  };
  // Ticked rows. Scoped to what is on screen, so a filter cannot hide
  // something that is still selected.
  const sel = useSelection(clients);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cr, ur] = await Promise.all([
        clientsAPI.getAll({ search:search||undefined, type:typeFilter||undefined, status:statFilter||undefined,
                            scope: scope === 'deleted' ? 'deleted' : undefined, mine: scope === 'mine' ? 'true' : undefined, limit: paging.limit, offset: paging.offset }),
        usersAPI.getAll(),
      ]);
      setClients(cr.data); setUsers(ur.data||[]);
      paging.readHeaders(cr);
    } catch(e) { toast.error(apiError(e, 'Something went wrong loading this.')); }
    finally { setLoading(false); }
  }, [search, typeFilter, statFilter, scope, paging.offset]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditClient(null); setForm({ ...defaultForm }); setModal(true); };
  const openEdit   = (c)  => { setEditClient(c); setForm({ ...defaultForm, ...c, date_of_birth: c.date_of_birth?.slice(0,10)||'' }); setModal(true); };

  const handleSave = async () => {
    const regErr = validateRegNo(form.registration_number);
    const taxErr = validateTaxId(form.tax_id);
    if (regErr || taxErr) { setErrors({ registration_number:regErr, tax_id:taxErr }); return; }
    setSaving(true);
    try {
      if (editClient) await clientsAPI.update(editClient.id, form);
      else            await clientsAPI.create(form);
      toast.success('Client saved');
      setModal(false); load();
    } catch(e) { toast.error(apiError(e, 'Failed to save client.')); }
    finally { setSaving(false); }
  };

  /** Deleting a client detaches its projects rather than removing them, so
   *  say how many were touched instead of doing it silently. */
  const handleDelete = async () => {
    try {
      const r = await clientsAPI.delete(delId);
      toast.success('Client deleted');
      const n = r.data?.projects_unlinked;
      if (n) toast.warning(`Client deleted. ${n} project${n===1?' was':'s were'} detached and kept.`);
    } catch(e) {
      toast.error(apiError(e, 'Could not delete that client.'));
    } finally { setDelId(null); load(); }
  };

  /** Matches the API: your own, or Admin level. */
  const canDelete = (c) => c?.created_by === me?.id || isAdminRole(me?.role);

  const clientName = (c) => c.type === 'company' ? c.company_name : `${c.first_name||''} ${c.last_name||''}`.trim();
  const clientInitials = (c) => clientName(c).split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();

  return (
    <div className="space-y-5">
      <PageHeader title="Clients" subtitle={`${clients.length} client${clients.length!==1?'s':''} registered`}
        actions={
          canWrite && (<button onClick={openCreate} className="btn-primary flex items-center gap-1.5 text-sm">
            <PlusIc/> Add Client
          </button>)
        }/>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <SearchInput value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search clients…"/>
        {/* All / Mine / Deleted. Deleted shows only what you could have seen
            while it was live — deletion must never reveal something hidden. */}
        <div className="flex rounded-lg border overflow-hidden text-sm self-start"
          style={{ borderColor:'var(--border)' }}>
          {[['all','All'],['mine','Mine'],['deleted','Deleted']].map(([v,l]) => (
            <button key={v} onClick={() => setScope(v)}
              className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1.5 ${scope===v ? 'bg-brand-500 text-white' : ''}`}
              style={scope!==v ? { color:'var(--text-secondary)', background:'var(--surface)' } : {}}>
              {v === 'deleted' && <Trash2 size={13} />}
              {l}
              {scope===v && <span className="font-bold">{clients.length}</span>}
            </button>
          ))}
        </div>

        <select className="select w-36 text-sm" value={typeFilter} onChange={e=>setTypeFilter(e.target.value)}>
          <option value="">All Types</option>
          <option value="company">Company</option>
          <option value="individual">Individual</option>
        </select>
        <select className="select w-36 text-sm" value={statFilter} onChange={e=>setStatFilter(e.target.value)}>
          <option value="">All Status</option>
          {['Active','Prospect','Inactive','Archived'].map(s=><option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          {label:'Total',    v:clients.length,                                          color:'#2E539F'},
          {label:'Companies',v:clients.filter(c=>c.type==='company').length,            color:'#5CC3BC'},
          {label:'Individuals',v:clients.filter(c=>c.type==='individual').length,        color:'#64748b'},
          {label:'Active',   v:clients.filter(c=>c.status==='Active').length,           color:'#10b981'},
        ].map(s=>(
          <div key={s.label} className="card px-4 py-3">
            <p className="text-xl font-bold" style={{ color:s.color }}>{s.v}</p>
            <p className="text-xs" style={{ color:'var(--text-secondary)' }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Clients Table */}
      {loading ? (
        <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/></div>
      ) : clients.length === 0 ? (
        <EmptyState icon={<Building2 size={18}/>} title="No clients yet" message="Add your first client to get started" action={canWrite && (<button onClick={openCreate} className="btn-primary text-sm">Add Client</button>)}/>
      ) : (
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
                  <th className="px-4 py-3 w-10">
                    <SelectAll allChecked={sel.allChecked} someChecked={sel.someChecked}
                      onToggle={sel.toggleAll} />
                  </th>
                  {['Client','Type','Industry','Email','Phone','Location','Status','Projects','Actions'].map(h=>(
                    <th key={h} className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider whitespace-nowrap"
                      style={{ color:'var(--text-secondary)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {clients.map(client=>(
                  <tr key={client.id}
                    className="border-b hover:bg-[var(--surface-alt)] cursor-pointer transition-colors group"
                    style={{ borderColor:'var(--border)' }}
                    onClick={()=>navigate(`/clients/${client.id}`)}>
                    <td className="py-3 px-4" onClick={e => e.stopPropagation()}>
                      <RowCheck checked={sel.isSelected(client.id)}
                        onToggle={() => sel.toggle(client.id)}
                        label={client.company_name || client.last_name} />
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-white text-xs flex-shrink-0"
                          style={{ background:client.type==='company'?'#2E539F':'#64748b' }}>
                          {clientInitials(client)}
                        </div>
                        <div>
                          <p className="font-semibold" style={{ color:'var(--text)' }}>{clientName(client)}</p>
                          {client.contact_person && <p className="text-xs" style={{ color:'var(--text-secondary)' }}>{client.contact_person}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <span className="text-xs px-2 py-1 rounded-full"
                        style={{ background:'var(--surface-alt)', color:'var(--text-secondary)' }}>
                        {client.type==='company'?'🏢 Company':'👤 Individual'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-xs" style={{ color:'var(--text-secondary)' }}>{client.industry||'—'}</td>
                    <td className="py-3 px-4 text-xs" style={{ color:'var(--text-secondary)' }}>{client.email||'—'}</td>
                    <td className="py-3 px-4 text-xs whitespace-nowrap" style={{ color:'var(--text-secondary)' }}>{client.phone||'—'}</td>
                    <td className="py-3 px-4 text-xs" style={{ color:'var(--text-secondary)' }}>
                      {[client.city,client.country].filter(Boolean).join(', ')||'—'}
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-xs px-2.5 py-1 rounded-full font-semibold"
                        style={{ background:STATUS_COLOR[client.status]+'20', color:STATUS_COLOR[client.status] }}>
                        {client.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center text-xs font-semibold" style={{ color:'var(--text)' }}>
                      {client.project_count}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {scope === 'deleted' ? (
                          <>
                          {/* In the bin the only sensible action is to put it
                             back. Editing or deleting it again are nonsense. */}
                          <button onClick={e=>{e.stopPropagation();restore(client);}}
                            className="text-xs px-2.5 py-1 rounded-lg border flex items-center gap-1 transition-colors hover:bg-[var(--surface-alt)]"
                            style={{ borderColor:'var(--border)', color:'#2E539F' }}>
                            <RotateCcw size={12} /> Restore
                          </button>
                          {/* The second delete. It takes this out of YOUR
                              Deleted tab and nothing more — an administrator
                              can still get it back. */}
                          <button onClick={e=>{e.stopPropagation();discard(client);}}
                            className="text-xs px-2.5 py-1 rounded-lg border flex items-center gap-1 transition-colors hover:bg-red-50 dark:hover:bg-red-900/20"
                            style={{ borderColor:'var(--border)', color:'#ef4444' }}>
                            <Trash2 size={12} /> Delete
                          </button>
                          </>
                        ) : (
                          <>
                          <button onClick={e=>{e.stopPropagation();openEdit(client);}}

                            className="text-xs px-2.5 py-1 rounded-lg border transition-colors hover:bg-[var(--surface-alt)]"

                            style={{ borderColor:'var(--border)', color:'var(--text-secondary)' }}>Edit</button>

                          {canDelete(client) && (

                            <button onClick={e=>{e.stopPropagation();setDelId(client.id);}}

                              className="text-xs px-2.5 py-1 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors">Del</button>

                          )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={()=>setModal(false)}/>
          <div className="relative bg-[var(--surface)] rounded-2xl shadow-2xl w-full max-w-2xl z-10 max-h-[90vh] flex flex-col"
            style={{ border:'1px solid var(--border)' }}>
            <div className="px-6 py-4 border-b flex-shrink-0" style={{ borderColor:'var(--border)' }}>
              <h3 className="font-bold text-base" style={{ color:'var(--text)' }}>
                {editClient ? 'Edit Client' : 'New Client'}
              </h3>
            </div>
            <div className="p-6 overflow-y-auto space-y-4">

              {/* Type selector */}
              {(
                /* Shown on edit too: the API accepts a type change, and hiding
                   the control was the only reason it could not be made. */
                <div>
                  <label className="label">Client Type</label>
                  <div className="flex rounded-xl overflow-hidden border" style={{ borderColor:'var(--border)' }}>
                    {[{k:'company',l:'🏢 Company'},{k:'individual',l:'👤 Individual'}].map(t=>(
                      <button key={t.k} type="button" onClick={()=>setForm(f=>({...f,type:t.k,
                          // Clear the other type's identifying fields, so a company
                          // name cannot linger on an individual and be saved by accident.
                          ...(t.k==='company'
                            ? { first_name:'', last_name:'', national_id:'', date_of_birth:'' }
                            : { company_name:'', registration_number:'', tax_id:'', industry:'', website:'' })}))}
                        className={`flex-1 py-2.5 text-sm font-medium transition-colors ${form.type===t.k?'bg-brand-500 text-white':''}`}
                        style={form.type!==t.k?{color:'var(--text-secondary)',background:'var(--surface-alt)'}:{}}>
                        {t.l}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Company fields */}
              {form.type === 'company' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="label">Company Name <span className="text-red-500">*</span></label>
                    <input className="input" value={form.company_name} onChange={e=>setForm(f=>({...f,company_name:e.target.value}))} placeholder="e.g. BRD Rwanda"/>
                  </div>
                  <div>
                    <label className="label">Registration No.</label>
                    <input className={`input ${errors.registration_number?'border-red-400':''}`}
                      value={form.registration_number}
                      onChange={e=>{
                        setForm(f=>({...f,registration_number:e.target.value}));
                        setErrors(er=>({...er,registration_number:validateRegNo(e.target.value)}));
                      }}/>
                    {errors.registration_number && <p className="text-xs text-red-500 mt-1">{errors.registration_number}</p>}
                  </div>
                  <div>
                    <label className="label">Tax ID / TIN</label>
                    <input className={`input ${errors.tax_id?'border-red-400':''}`}
                      value={form.tax_id}
                      onChange={e=>{
                        setForm(f=>({...f,tax_id:e.target.value}));
                        setErrors(er=>({...er,tax_id:validateTaxId(e.target.value)}));
                      }}/>
                    {errors.tax_id && <p className="text-xs text-red-500 mt-1">{errors.tax_id}</p>}
                  </div>
                  <div><label className="label">Industry</label>
                    <select className="select" value={form.industry} onChange={e=>setForm(f=>({...f,industry:e.target.value}))}>
                      <option value="">Select...</option>
                      {INDUSTRIES.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div><label className="label">Website</label>
                    <input className="input" value={form.website} onChange={e=>setForm(f=>({...f,website:e.target.value}))} placeholder="https://"/>
                  </div>
                  <div className="col-span-2"><label className="label">Primary Contact Person</label>
                    <input className="input" value={form.contact_person} onChange={e=>setForm(f=>({...f,contact_person:e.target.value}))} placeholder="Full name of main contact"/>
                  </div>
                </div>
              )}

              {/* Individual fields */}
              {form.type === 'individual' && (
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="label">First Name <span className="text-red-500">*</span></label>
                    <input className="input" value={form.first_name} onChange={e=>setForm(f=>({...f,first_name:e.target.value}))}/>
                  </div>
                  <div><label className="label">Last Name <span className="text-red-500">*</span></label>
                    <input className="input" value={form.last_name} onChange={e=>setForm(f=>({...f,last_name:e.target.value}))}/>
                  </div>
                  <div><label className="label">National ID / Passport</label>
                    <input className="input" value={form.national_id} onChange={e=>setForm(f=>({...f,national_id:e.target.value}))}/>
                  </div>
                  <div><label className="label">Date of Birth</label>
                    <input type="date" className="input" value={form.date_of_birth} onChange={e=>setForm(f=>({...f,date_of_birth:e.target.value}))}/>
                  </div>
                  <div><label className="label">Gender</label>
                    <select className="select" value={form.gender} onChange={e=>setForm(f=>({...f,gender:e.target.value}))}>
                      <option value="">Select...</option>
                      {['Male','Female','Other','Prefer not to say'].map(g=><option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {/* Common fields */}
              <div className="grid grid-cols-2 gap-4">
                <div><label className="label">Email</label>
                  <input type="email" className="input" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))}/>
                </div>
                <div><label className="label">Phone</label>
                  <input className="input" value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} placeholder="+250…"/>
                </div>
                <div><label className="label">Alt. Phone</label>
                  <input className="input" value={form.phone_alt} onChange={e=>setForm(f=>({...f,phone_alt:e.target.value}))}/>
                </div>
                <div><label className="label">Country</label>
                  <select className="select" value={form.country} onChange={e=>setForm(f=>({...f,country:e.target.value,city:''}))}>
                    <option value="">Select country...</option>
                    {COUNTRIES.map(c=><option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div><label className="label">City</label>
                  {getCities(form.country).length > 0 ? (
                    <select className="select" value={form.city} onChange={e=>setForm(f=>({...f,city:e.target.value}))}>
                      <option value="">Select city...</option>
                      {getCities(form.country).map(ci=><option key={ci} value={ci}>{ci}</option>)}
                    </select>
                  ) : (
                    <input className="input" value={form.city} onChange={e=>setForm(f=>({...f,city:e.target.value}))} placeholder="Enter city name"/>
                  )}
                </div>
                <div><label className="label">Status</label>
                  <select className="select" value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>
                    {['Active','Prospect','Inactive','Archived'].map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="col-span-2"><label className="label">Address</label>
                  <input className="input" value={form.address} onChange={e=>setForm(f=>({...f,address:e.target.value}))}/>
                </div>
                <div><label className="label">Account Manager</label>
                  <select className="select" value={form.account_manager_id} onChange={e=>setForm(f=>({...f,account_manager_id:e.target.value}))}>
                    <option value="">None</option>
                    {users.filter(u=>u.is_active).map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div className="col-span-2"><label className="label">Notes</label>
                  <textarea className="input" rows={2} value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-2 flex-shrink-0" style={{ borderColor:'var(--border)' }}>
              <button onClick={()=>setModal(false)} className="btn-secondary">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2">
                {saving && <span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}
                {editClient ? 'Save Changes' : 'Create Client'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog isOpen={!!delId} onClose={()=>setDelId(null)} onConfirm={handleDelete}
        title="Delete Client"
        message="This will unlink all associated projects. The projects themselves will not be deleted. Continue?"/>

    <Pager {...paging} onChange={paging.setOffset} noun="clients" />
    <BulkBar bulk={clientsAPI.bulk} view={scope === 'deleted' ? 'deleted' : 'live'}
      selected={sel.selected} label="client" labelPlural="clients"
      onClear={sel.clear} onDone={load} />
    </div>
  );
}
